import { useCallback, useEffect, useState } from 'react';

import safeEnableAdsTracking from 'bundles/event-tracking/safeEnableAdsTracking';

type UseMarketingScriptsReturn = {
  isAdsTrackingEnabled: boolean;
};

const useMarketingScripts = (): UseMarketingScriptsReturn => {
  const [isAdsTrackingEnabled, setAdsTracking] = useState(false);

  const enableAdsTracking = useCallback<(timeout?: number) => void>(
    (timeout = 1000) => {
      if (!isAdsTrackingEnabled) {
        setAdsTracking(true);
        // As a general buffer, wait one sec before enabling ad tracking.
        setTimeout(() => {
          safeEnableAdsTracking();
        }, timeout);
      }
    },
    [isAdsTrackingEnabled]
  );

  const checkForPerformanceTimings = useCallback<() => void>(() => {
    if (!isAdsTrackingEnabled) {
      if (window && window.performance?.timing?.loadEventEnd > 0) {
        enableAdsTracking(5000);
      } else {
        setTimeout(checkForPerformanceTimings, 100);
      }
    }
  }, [isAdsTrackingEnabled, enableAdsTracking]);

  useEffect(() => {
    // Only add our marketing scripts when user has interacted with the page.
    // This is safe way to ensure our TTI doesn't get hammered by all of our marketing scripts loading on page load.
    const handleScroll = () => enableAdsTracking();
    const handleClick = () => enableAdsTracking();

    window.addEventListener('scroll', handleScroll, { once: true });
    window.addEventListener('click', handleClick, { once: true });

    // As a catch, check for window.performance.timing.loadEventEnd (page is completely done loading) to add tracking tags.
    checkForPerformanceTimings();

    return () => {
      // Cleanup event listeners if component unmounts before they fire
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
    };
  }, [enableAdsTracking, checkForPerformanceTimings]);

  return { isAdsTrackingEnabled };
};

export default useMarketingScripts;
